import { Component, EventEmitter, Output, ViewChild, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  AuthorizationDeliveryMode,
  ProspectCodeRequest,
  TokenResponse,
  UserLoginInfo
} from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { AdvertisementSourceDataModel } from '@signature/signature.extentions';
import { combineLatest } from 'rxjs';
import { AUTH_TOO_MANY_REQUESTS_MESSAGE } from '../../../../shared/constants/auth-messages';
import { RegistrationModel } from '../../../../shared/extensions/user-data-extensions';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { hasPartnerCodeValidationError } from '../../../../store/client-store/client.helpers';
import {
  ClientAuthErrorCode,
  ClientAuthorizationStatus,
  RequestCodeClientAuthError,
  ValidateCodeClientAuthError
} from '../../../../store/client-store/client.models';
import { AdvertisementSourceClientComponent } from '../advertisement-source-client/advertisement-source-client.component';
import { RegistrationFormComponent } from '../registration-form/registration-form.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-authentication-prospect',
  templateUrl: './authentication-prospect.component.html',
  styleUrls: ['./authentication-prospect.component.scss']
})
export class AuthenticationProspectComponent {
  private readonly clientFacade = inject(ClientFacade);
  private readonly customerService = inject(CustomerService);

  @ViewChild(RegistrationFormComponent) registrationFormComponent?: RegistrationFormComponent;
  advertisementSourceClient?: AdvertisementSourceClientComponent;
  private pendingPartnerCodeError = false;

  @ViewChild(AdvertisementSourceClientComponent)
  set advertisementSourceComponent(component: AdvertisementSourceClientComponent | undefined) {
    this.advertisementSourceClient = component;
    if (component && this.pendingPartnerCodeError) {
      component.markPartnerCodeInvalid();
      this.pendingPartnerCodeError = false;
    }
  }

  @Output() completed = new EventEmitter<string>();

  requestCodeAuthErrorMessage: string | null = null;
  requestCodeAuthError: RequestCodeClientAuthError | null = null;
  clientAuthResponse: UserLoginInfo | null = null;
  tokenResponse: TokenResponse | null = null;
  clientAuthorizationStatus = ClientAuthorizationStatus.Idle;
  isOtpAccessBlocked = false;
  isSubmittingClientAuthRequest = false;
  isAuthorizingClient = false;
  isSavingProspectProfile = false;
  isFinalizingProspectProfile = false;
  readonly tooManyRequestsMessage = AUTH_TOO_MANY_REQUESTS_MESSAGE;

  constructor() {
    this.initSubscriptions();
  }

  async onSubmitRegistration(model: RegistrationModel): Promise<void> {
    if (this.isBusy) {
      return;
    }

    if (this.advertisementSourceClient && !this.advertisementSourceClient.isMifFormValid) {
      this.advertisementSourceClient.markAllAsTouched();
      return;
    }

    const advertisementSourceModel =
      this.advertisementSourceClient?.mifForm?.getRawValue() as AdvertisementSourceDataModel;

    this.isSavingProspectProfile = true;

    try {
      const registrationModel = {
        ...model,
        //mobilePhone: '+995511113389', // todo: del
        isMember: false
      } as RegistrationModel;

      if (this.hasAuthorizedProspectSession) {
        await this.persistProspectData(registrationModel, advertisementSourceModel);
        return;
      }

      this.clientFacade.setPendingProspectRegistration(registrationModel);
      this.clientFacade.setPendingProspectAdvertisementSource(advertisementSourceModel);

      const request = new ProspectCodeRequest({
        phoneNumber: model.mobilePhone,
        email: model.email,
        deliveryMode: AuthorizationDeliveryMode.Sms
      });

      this.clientFacade.submitProspectAuthentication(request);
    } finally {
      this.isSavingProspectProfile = false;
    }
  }

  onOtpCancel(): void {
    this.clientFacade.cancelClientAuthentication();
  }

  private initSubscriptions(): void {
    this.clientFacade.authResponse$.pipe(untilDestroyed(this)).subscribe(authResponse => {
      this.clientAuthResponse = authResponse;
    });

    this.clientFacade.requestCodeAuthError$
      .pipe(untilDestroyed(this))
      .subscribe((authError: RequestCodeClientAuthError | null) => {
        this.requestCodeAuthError = authError;
        this.requestCodeAuthErrorMessage = this.getRequestCodeAuthErrorMessage(authError);
      });

    this.clientFacade.tokenResponse$.pipe(untilDestroyed(this)).subscribe(tokenResponse => {
      this.tokenResponse = tokenResponse;
    });

    this.clientFacade.validateCodeAuthError$
      .pipe(untilDestroyed(this))
      .subscribe((authError: ValidateCodeClientAuthError | null) => {
        this.isOtpAccessBlocked = authError?.errorCode === ClientAuthErrorCode.AccessBlocked;

        if (authError && !this.clientAuthResponse) {
          this.clientAuthResponse = null;
        }
      });

    this.clientFacade.clientAuthorizationStatus$.pipe(untilDestroyed(this)).subscribe(clientAuthorizationStatus => {
      this.clientAuthorizationStatus = clientAuthorizationStatus;
    });

    this.clientFacade.isSubmittingClientAuthRequest$
      .pipe(untilDestroyed(this))
      .subscribe(isSubmittingClientAuthRequest => {
        this.isSubmittingClientAuthRequest = isSubmittingClientAuthRequest;
      });

    this.clientFacade.isAuthorizingClient$.pipe(untilDestroyed(this)).subscribe(isAuthorizingClient => {
      this.isAuthorizingClient = isAuthorizingClient;
    });

    combineLatest([
      this.clientFacade.tokenResponse$,
      this.clientFacade.clientAuthorizationStatus$,
      this.clientFacade.pendingProspectRegistration$,
      this.clientFacade.pendingProspectAdvertisementSource$
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([tokenResponse, clientAuthorizationStatus, registrationModel, advertisementSourceModel]) => {
        if (
          !tokenResponse ||
          clientAuthorizationStatus !== ClientAuthorizationStatus.Authorized ||
          !registrationModel ||
          !advertisementSourceModel ||
          this.isFinalizingProspectProfile
        ) {
          return;
        }

        void this.persistProspectData(registrationModel, advertisementSourceModel);
      });
  }

  private async persistProspectData(
    registrationModel: RegistrationModel,
    advertisementSourceModel: AdvertisementSourceDataModel
  ): Promise<void> {
    this.isFinalizingProspectProfile = true;

    try {
      const userId = await this.customerService.updateRegistrationModel(registrationModel);
      await this.customerService.updateAdvertisementSourceDataModel(advertisementSourceModel);
      this.clientFacade.clearPendingProspectData();
      this.completed.emit(userId);
    } catch (error) {
      if (!hasPartnerCodeValidationError(error)) {
        throw error;
      }

      this.pendingPartnerCodeError = !this.advertisementSourceClient;
      this.advertisementSourceClient?.markPartnerCodeInvalid();
    } finally {
      this.isFinalizingProspectProfile = false;
    }
  }

  private getRequestCodeAuthErrorMessage(authError: RequestCodeClientAuthError | null): string | null {
    if (!authError) {
      return null;
    }

    if (authError.errorCode === ClientAuthErrorCode.CodeDeliveryFailed) {
      return "Impossible d'envoyer le code. Veuillez reessayer plus tard.";
    }

    if (authError.errorCode === ClientAuthErrorCode.InvalidCredentials) {
      return 'Les informations saisies sont invalides.';
    }

    if (authError.errorCode === ClientAuthErrorCode.TooManyRequests) {
      return null;
    }

    return authError.detail;
  }

  get showTooManyRequestsScreen(): boolean {
    return this.requestCodeAuthError?.errorCode === ClientAuthErrorCode.TooManyRequests;
  }

  get showOtpCheckScreen(): boolean {
    return (
      !!this.clientAuthResponse &&
      this.clientAuthorizationStatus !== ClientAuthorizationStatus.Authorized &&
      (this.clientAuthorizationStatus !== ClientAuthorizationStatus.Blocked || this.isOtpAccessBlocked) &&
      this.clientAuthorizationStatus !== ClientAuthorizationStatus.Unauthorized
    );
  }

  get isBusy(): boolean {
    return (
      this.isSavingProspectProfile ||
      this.isSubmittingClientAuthRequest ||
      this.isAuthorizingClient ||
      this.isFinalizingProspectProfile
    );
  }

  get hasAuthorizedProspectSession(): boolean {
    return !!this.tokenResponse && this.clientAuthorizationStatus === ClientAuthorizationStatus.Authorized;
  }
}
